import { ChangeEvent, ReactNode, useState } from 'react';
import { Box, ButtonBase, Collapse, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowDownOutlined, ArrowUpOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, parse } from 'date-fns';
import CheckCard from '../../../../components/ui/CheckCard';
import SegmentedControl, { SegmentOption } from '../../../../components/ui/SegmentedControl';
import { focusRingSx } from '../../../../components/ui/styleKit';
import { RADIUS, REDUCED_MOTION } from '../../../../components/ui/tokens';
import { PlatformApiCode } from '../../../../constants/platforms';
import { MasterOrderInfo } from '../../../../proto/trade-copier_pb';
import { Eyebrow, ink } from '../../../mcp/SignalDesk';
import SymbolAutocompleteWrapper from '../../components/SymbolAutocompleteWrapper';
import { mt4Options, mt5Options } from '../../AddCopierSection';
import { useSimulateActions, useSimulateMeta, useSimulateState } from '../SimulateTradeContext';
import {
  composeOrderType,
  EXPIRATION_DISPLAY_FORMAT,
  EXPIRATION_WIRE_FORMAT,
  MARKET_KIND,
  ORDER_KINDS,
  ORDER_SIDES,
  OrderKind,
  OrderSide,
  simulateOverlayZ,
  splitOrderType
} from '../constants';
import { SIMULATE_COPY } from '../content';
import { simulateFieldSx } from '../styles';
import { CustomSlaveSymbolField } from './CustomSlaveSymbolField';
import { DecimalField } from './DecimalField';

const { form } = SIMULATE_COPY;

// 34px tiles + padding + border = the 44px field height.
const TILE_HEIGHT_PX = 34;
const MORE_OPTIONS_ID = 'simulate-more-options';
const SIDE_PILL_ID = 'simulate-side-pill';
const KIND_PILL_ID = 'simulate-kind-pill';
const OVERLAY_SX = { zIndex: simulateOverlayZ } as const;
const SELECT_MENU_PROPS = { sx: OVERLAY_SX } as const;
const SHRINK_LABEL = { shrink: true } as const;
const TWO_COLUMNS = { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' };
const THREE_COLUMNS = { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' };

const SIDE_ICONS: Record<OrderSide, ReactNode> = { Buy: <ArrowUpOutlined />, Sell: <ArrowDownOutlined /> };
const SIDE_TONES: Record<OrderSide, SegmentOption<OrderSide>['tone']> = { Buy: 'success', Sell: 'error' };
const SIDE_OPTIONS: SegmentOption<OrderSide>[] = ORDER_SIDES.map((side) => ({
  value: side,
  tone: SIDE_TONES[side],
  label: (
    <>
      {SIDE_ICONS[side]}
      {form.sides[side]}
    </>
  )
}));
const KIND_OPTIONS: SegmentOption<OrderKind>[] = ORDER_KINDS.map((kind) => ({ value: kind, label: form.kinds[kind] }));

const parseExpiration = (s: string): Date | null => {
  if (!s) return null;
  const d = parse(s, EXPIRATION_WIRE_FORMAT, new Date());
  return isValid(d) ? d : null;
};

const formatExpiration = (d: Date | null): string => (d && isValid(d) ? format(d, EXPIRATION_WIRE_FORMAT) : '');

// Only MetaTrader masters report a placed type. Built per call, never at load: AddCopierSection imports
// this module (circular import), so its options are not initialised yet while this file evaluates.
const placedTypeOptions = (platform?: string): { name: string; value: string }[] => {
  const byPlatform: Partial<Record<PlatformApiCode, { name: string; value: string }[]>> = { 'MT-4': mt4Options, 'MT-5': mt5Options };
  return byPlatform[platform as PlatformApiCode] ?? [];
};

const Labeled = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack spacing={1}>
    <Eyebrow>{label}</Eyebrow>
    {children}
  </Stack>
);

const FieldGrid = ({ columns, children }: { columns: typeof TWO_COLUMNS; children: ReactNode }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 2 }}>{children}</Box>
);

export const MasterOrderForm = () => {
  const theme = useTheme();
  const fieldSx = simulateFieldSx(theme);
  const { masterOrder, customSlaveSymbol } = useSimulateState();
  const { setMasterOrderField } = useSimulateActions();
  const { masterAccountId, masterAccount } = useSimulateMeta();
  const [moreOpen, setMoreOpen] = useState(false);

  const placedTypes = placedTypeOptions(masterAccount?.platform);
  const { side, kind } = splitOrderType(masterOrder.orderType);
  const pending = kind !== MARKET_KIND;
  const priceMissing = !masterOrder.openPrice && Boolean(masterOrder.stopLoss || masterOrder.takeProfit);
  const optionalSet = [
    masterOrder.magicNumber,
    masterOrder.comment.trim(),
    masterOrder.placedType,
    masterOrder.expiration,
    customSlaveSymbol.trim()
  ].filter(Boolean).length;

  const setSide = (next: OrderSide) => setMasterOrderField('orderType', composeOrderType(next, kind));
  const setKind = (next: OrderKind) => {
    setMasterOrderField('orderType', composeOrderType(side, next));
    // Expiry only applies to pending orders; a filled pending order is sent as market.
    if (next === MARKET_KIND) setMasterOrderField('expiration', '');
    else setMasterOrderField('isPendingFill', false);
  };

  const handleNumber =
    <K extends keyof MasterOrderInfo.AsObject>(key: K) =>
    (value: number) =>
      setMasterOrderField(key, value as MasterOrderInfo.AsObject[K]);

  const handleString =
    <K extends keyof MasterOrderInfo.AsObject>(key: K) =>
    (e: ChangeEvent<HTMLInputElement>) =>
      setMasterOrderField(key, e.target.value as MasterOrderInfo.AsObject[K]);

  return (
    <Stack spacing={2.5}>
      <FieldGrid columns={TWO_COLUMNS}>
        <Labeled label={form.side}>
          <SegmentedControl
            options={SIDE_OPTIONS}
            value={side}
            onChange={setSide}
            ariaLabel={form.side}
            layoutId={SIDE_PILL_ID}
            tileHeight={TILE_HEIGHT_PX}
          />
        </Labeled>
        <Labeled label={form.kind}>
          <SegmentedControl
            options={KIND_OPTIONS}
            value={kind}
            onChange={setKind}
            ariaLabel={form.kind}
            layoutId={KIND_PILL_ID}
            tileHeight={TILE_HEIGHT_PX}
          />
        </Labeled>
      </FieldGrid>

      <FieldGrid columns={TWO_COLUMNS}>
        <SymbolAutocompleteWrapper
          id="simulate-master-symbol"
          label={form.symbol}
          selectedAccountId={masterAccountId}
          defaultValue={masterOrder.symbol}
          disabled={!masterAccountId}
          sx={fieldSx}
          popperSx={OVERLAY_SX}
          onOptionSelect={(v) => setMasterOrderField('symbol', v?.name ?? '')}
        />
        <DecimalField
          fullWidth
          sx={fieldSx}
          label={form.lots}
          placeholder={form.placeholders.lots}
          value={masterOrder.lots}
          onValueChange={handleNumber('lots')}
          InputLabelProps={SHRINK_LABEL}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">
                  {form.lotsUnit}
                </Typography>
              </InputAdornment>
            )
          }}
        />
      </FieldGrid>

      <Stack spacing={1}>
        <FieldGrid columns={THREE_COLUMNS}>
          <DecimalField
            fullWidth
            sx={fieldSx}
            label={form.openPrice}
            placeholder={form.placeholders.number}
            value={masterOrder.openPrice}
            onValueChange={handleNumber('openPrice')}
            InputLabelProps={SHRINK_LABEL}
          />
          <DecimalField
            fullWidth
            sx={fieldSx}
            label={form.stopLoss}
            placeholder={form.placeholders.number}
            value={masterOrder.stopLoss}
            onValueChange={handleNumber('stopLoss')}
            InputLabelProps={SHRINK_LABEL}
          />
          <DecimalField
            fullWidth
            sx={fieldSx}
            label={form.takeProfit}
            placeholder={form.placeholders.number}
            value={masterOrder.takeProfit}
            onValueChange={handleNumber('takeProfit')}
            InputLabelProps={SHRINK_LABEL}
          />
        </FieldGrid>
        {priceMissing && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: ink(theme, 'warning') }}>
            <InfoCircleOutlined style={{ fontSize: 12 }} />
            <Typography variant="caption">{form.priceHint}</Typography>
          </Stack>
        )}
      </Stack>

      {!pending && (
        <CheckCard checked={masterOrder.isPendingFill} onToggle={() => setMasterOrderField('isPendingFill', !masterOrder.isPendingFill)}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {form.pendingFill.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {form.pendingFill.hint}
            </Typography>
          </Box>
        </CheckCard>
      )}

      <Stack spacing={1.5}>
        <ButtonBase
          onClick={() => setMoreOpen((open) => !open)}
          aria-expanded={moreOpen}
          aria-controls={MORE_OPTIONS_ID}
          sx={{
            ...focusRingSx(theme),
            alignSelf: 'flex-start',
            gap: 0.75,
            px: 0.5,
            py: 0.25,
            borderRadius: RADIUS.tile,
            fontSize: 13,
            fontWeight: 600,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' }
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'flex',
              fontSize: 10,
              transition: 'transform .2s ease',
              transform: moreOpen ? 'none' : 'rotate(-90deg)',
              [REDUCED_MOTION]: { transition: 'none' }
            }}
          >
            <DownOutlined />
          </Box>
          {form.more}
          {optionalSet > 0 && (
            <Typography component="span" variant="caption" sx={{ color: ink(theme), fontWeight: 600 }}>
              · {form.moreSet(optionalSet)}
            </Typography>
          )}
        </ButtonBase>

        <Collapse in={moreOpen} id={MORE_OPTIONS_ID}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FieldGrid columns={TWO_COLUMNS}>
              <DecimalField
                fullWidth
                sx={fieldSx}
                label={form.magic}
                placeholder={form.placeholders.number}
                integer
                value={masterOrder.magicNumber}
                onValueChange={handleNumber('magicNumber')}
                InputLabelProps={SHRINK_LABEL}
              />
              <TextField
                fullWidth
                sx={fieldSx}
                label={form.comment}
                value={masterOrder.comment}
                onChange={handleString('comment')}
                InputLabelProps={SHRINK_LABEL}
              />
            </FieldGrid>

            {(placedTypes.length > 0 || pending) && (
              <FieldGrid columns={TWO_COLUMNS}>
                {placedTypes.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    sx={fieldSx}
                    label={form.placedType}
                    value={masterOrder.placedType}
                    onChange={handleString('placedType')}
                    InputLabelProps={SHRINK_LABEL}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    {placedTypes.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {pending && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label={form.expiration}
                      value={parseExpiration(masterOrder.expiration)}
                      onChange={(d) => {
                        const date = d as Date | null;
                        if (date && isValid(date) && date.getTime() <= Date.now()) {
                          setMasterOrderField('expiration', '');
                          return;
                        }
                        setMasterOrderField('expiration', formatExpiration(date));
                      }}
                      inputFormat={EXPIRATION_DISPLAY_FORMAT}
                      ampm={false}
                      minDateTime={new Date()}
                      disablePast
                      PopperProps={{ sx: OVERLAY_SX }}
                      DialogProps={{ sx: OVERLAY_SX }}
                      renderInput={(params) => {
                        const parsed = parseExpiration(masterOrder.expiration);
                        const isPast = !!parsed && parsed.getTime() <= Date.now();
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            sx={fieldSx}
                            error={params.error || isPast}
                            helperText={isPast ? form.expirationPast : params.helperText}
                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                )}
              </FieldGrid>
            )}

            <CustomSlaveSymbolField />
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  );
};
